/**
 * Helper form component for rendering email inputs.  This handles it's own state
 * and validation. Will only return email to parent component if valid.
 *
 */

// import primary libraries
import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import CircleErrorIcon from "../SVG/CircleErrorIcon";

const EmailInput = ({
    change,
    disabled,
    helpText,
    label,
    name,
    placeholder,
    required,
    value,
    handleError,
    onTheFlyCheck = true,
    propsErrorText = '',
    ...inputProps
}) => {
  const [email, setEmail] = useState(value);
  const [error, setError] = useState(null);
  const [viewError, setViewError] = useState(handleError);

  useEffect(() => {
    setViewError(handleError)
  }, [handleError]);

  const onChange = e => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    setViewError('');
    const emailIsValid = validateEmail(newEmail);

    if(emailIsValid) {
      // email is valid, clear error and send the valid email as an event
      setError(null);
      change({
        target: {
          name: name,
          value: newEmail
        }
      });
    } else {
      // email is invalid, set error and send an empty string as an event
      if (onTheFlyCheck) {
          setError("Please enter a valid email address");
      }
      change({
        target: {
          name: name,
          value: ""
        }
      });
    }
  }

  return (
    <div className="relative z-0 w-full mb-4 text-left lg:w-auto">
      { label ? (
        <label
          htmlFor={name}
          className="px-2 pt-1 text-xs absolute top-0 text-gray-500 bg-transparent z-10"
        >
          {label} <sup className="text-red-500">{required ? '*' : null}</sup>
        </label>
        )
        :
        null
      }
      <div className="relative">
          <input
              disabled={disabled}
              name={name}
              onChange={onChange}
              placeholder={placeholder}
              required={required}
              type="email"
              value={email}
              className={`px-2 text-base ${label ? 'pt-5 pb-1' : 'pt-2 pb-2'} block w-full mt-0 border rounded-md border-stone-200 focus:border-2 focus:border-blue-600 focus:outline-none active:outline-none active:border-2 active:border-blue-600 max-h-[40px]${propsErrorText ? ' focus:border-red-600 active:border-red-600 border-red-600' : ''}`}
              {...inputProps}
          />
          {propsErrorText && <div className="absolute bottom-[11px] right-3">
              <CircleErrorIcon className="max-w-[16px] min-w-[16px] max-h-[16px] min-h-[16px]"/>
          </div>}
      </div>
      {error && <div className="text-xs text-red-800">{error}</div>}
      {viewError && <div className="text-xs text-red-800">{viewError}</div>}
      {helpText && <small className="text-xs text-gray-500"><em>{helpText}</em></small>}
      {propsErrorText && <p className="text-xs text-red-600">{propsErrorText}</p>}
    </div>
  )
}

EmailInput.propTypes = {
  change: PropTypes.func.isRequired
  , disabled: PropTypes.bool
  , helpText: PropTypes.any
  , label: PropTypes.string
  , name: PropTypes.string.isRequired
  , placeholder: PropTypes.string
  , required: PropTypes.bool
  , value: PropTypes.string.isRequired
  , handleError: PropTypes.string
}

EmailInput.defaultProps = {
  disabled: false
  , helpText: null
  , label: ''
  , placeholder: ''
  , required: false
}

export default EmailInput;

// could move this to a util but it's probably never going to used outside this component
const validateEmail = email => {
  // Checks for ____@____.__
  const validEmailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return validEmailRegex.test(email);
}
